import "./styles/App.css";
import LionheartAppbar from "./components/Appbar";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { UserProvider } from "./context/UserContext";

// Import your pages to route between
import { ToastProvider } from "./context/ToastContext";
import HomePage from "./pages/HomePage";
import ProjectsPage from "./pages/projects/ProjectsPage";
import ProjectCreationPage from "./pages/projects/ProjectCreationPage";
import ProjectDetailPage from "./pages/projects/ProjectDetailPage";
import InsurancePage from "./pages/insurance/InsurancePage";
import InsuranceDashboard from "./pages/insurance/InsuranceDashboard";
import MeetingLogPage from "./pages/meetings/MeetingLogPage";
import MeetingCreationPage from "./pages/meetings/MeetingCreationPage";
import CandidateTrackerPage from "./pages/candidates/CandidateTrackerPage";
import CandidateCreationPage from "./pages/candidates/CandidateCreationPage";
import CandidateDetailPage from "./pages/candidates/CandidateDetailPage";
import { ThemeProvider } from "@emotion/react";
import { useState } from "react";
import darkTheme from "./theme";
import { Snackbar, Alert, CssBaseline, Box, Typography } from "@mui/material";
import ProfilePage from "./pages/ProfilePage";
import AdminPage from "./pages/AdminPage";
import MeetingDetailPage from "./pages/meetings/MeetingDetailPage";
import InsuranceRatesDashboard from "./pages/insurance/InsuranceRatesDashboard";
import ReimbursementAddPage from "./pages/reimbursements/ReimbursementAddPage";
import ReimbursementManagerPage from "./pages/reimbursements/ReimbursementManagerPage";
import ReimbursementDetailPage from "./pages/reimbursements/ReimbursementDetailPage";
import AccessDeniedPage from "./pages/RedirectPages/AccessDeniedPage";
import HoursAddPage from "./pages/invoice/HoursAddPage";
import NotificationPage from "./pages/NotificationPage";
import GenerateInvoice from "./pages/invoice/GenerateInvoice";
import PrivateRoute from "./components/PrivateRoute";
import { GitInfoDisplay } from "./components/GitInfoDisplay";

function App() {
  // Define shared state
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
    vertical: "bottom",
    horizontal: "center",
  });

  // Helper function for ease of calling
  const showToast = (message, severity = "success") => {
    setToast((prev) => ({ ...prev, open: true, message, severity }));
  };

  const handleCloseToast = (event, reason) => {
    if (reason === "clickaway") return;
    setToast((prev) => ({ ...prev, open: false }));
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <ToastProvider showToast={showToast}>
        <div className="App">
          <Router>
            <UserProvider>
              <LionheartAppbar />
              <Routes>
                {/* PUBLIC ROUTES */}

                {/* Home Page  */}
                <Route path="/" element={<HomePage />} />
                {/* Access Denied Page*/}
                <Route path="/unauthorized" element={<AccessDeniedPage />} />

                {/* PROTECTED ROUTES (any logged in user has access) */}

                {/* User Profile Page  */}
                <Route
                  path="/profile"
                  element={
                    <PrivateRoute>
                      <ProfilePage />
                    </PrivateRoute>
                  }
                />

                {/* Project routes */}
                <Route
                  path="/projects"
                  element={
                    <PrivateRoute>
                      <ProjectsPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/projects/create"
                  element={
                    <PrivateRoute>
                      <ProjectCreationPage />
                    </PrivateRoute>
                  }
                />

                {/* Dynamic Project Detail Pages */}
                <Route
                  path="/projects/:id"
                  element={
                    <PrivateRoute>
                      <ProjectDetailPage />
                    </PrivateRoute>
                  }
                />

                {/* Insurance route */}
                <Route
                  path="/insurance"
                  element={
                    <PrivateRoute>
                      <InsurancePage />
                    </PrivateRoute>
                  }
                />

                {/* Meeting routes */}
                <Route
                  path="/meetings"
                  element={
                    <PrivateRoute>
                      <MeetingLogPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/meetings/add"
                  element={
                    <PrivateRoute>
                      <MeetingCreationPage />
                    </PrivateRoute>
                  }
                />

                {/* Dynamic Meeting Detail Pages */}
                <Route
                  path="/meetings/:id"
                  element={
                    <PrivateRoute>
                      <MeetingDetailPage />
                    </PrivateRoute>
                  }
                />

                {/* Reimbursement Page */}
                <Route
                  path="/reimbursements"
                  element={
                    <PrivateRoute>
                      <ReimbursementManagerPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/reimbursements/add"
                  element={
                    <PrivateRoute>
                      <ReimbursementAddPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/reimbursements/:id"
                  element={
                    <PrivateRoute>
                      <ReimbursementDetailPage />
                    </PrivateRoute>
                  }
                />

                {/* Hour tracking page */}
                <Route
                  path="/invoice/hours"
                  element={
                    <PrivateRoute>
                      <HoursAddPage />
                    </PrivateRoute>
                  }
                />

                {/* ADMIN ONLY ROUTES (Requires "Administrator" group) */}

                <Route
                  path="/insurance/dashboard"
                  element={
                    <PrivateRoute requiredGroup="Administrator">
                      <InsuranceDashboard />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/insurance/rates/dashboard"
                  element={
                    <PrivateRoute requiredGroup="Administrator">
                      <InsuranceRatesDashboard />
                    </PrivateRoute>
                  }
                />

                {/* Candidate Tracker routes */}
                <Route
                  path="/candidates/view"
                  element={
                    <PrivateRoute requiredGroup="Administrator">
                      <CandidateTrackerPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/candidates/add"
                  element={
                    <PrivateRoute requiredGroup="Administrator">
                      <CandidateCreationPage />
                    </PrivateRoute>
                  }
                />

                {/* Candidate Information Pages */}
                <Route
                  path="/candidates/view/:id"
                  element={
                    <PrivateRoute requiredGroup="Administrator">
                      <CandidateDetailPage />
                    </PrivateRoute>
                  }
                />

                {/* Admin Page */}
                <Route
                  path="/admin"
                  element={
                    <PrivateRoute requiredGroup="Administrator">
                      <AdminPage />
                    </PrivateRoute>
                  }
                />


                { /* Notification Page*/}
                <Route
                  path="/admin/notifications"
                  element={
                    <PrivateRoute requiredGroup="Administrator">
                      <NotificationPage />
                    </PrivateRoute>
                  }
                />

                {/* Invoice generation page */}
                <Route
                  path="/invoice/create"
                  element={
                    <PrivateRoute requiredGroup="Administrator">
                      <GenerateInvoice />
                    </PrivateRoute>
                  }
                />
              </Routes>
              <Box
                sx={{ textAlign: "center", padding: "20px", marginTop: "auto" }}
              >
                <Typography variant="body2">
                  <a href="/privacy-policy.html">Privacy Policy</a>
                </Typography>{" "}
              </Box>
              <GitInfoDisplay />
            </UserProvider>
          </Router>

          {/* Global Snackbar */}
          <Snackbar
            open={toast.open}
            autoHideDuration={2000}
            onClose={handleCloseToast}
            anchorOrigin={{
              vertical: toast.vertical || "bottom",
              horizontal: toast.horizontal || "center",
            }}
          >
            <Alert
              onClose={handleCloseToast}
              severity={toast.severity}
              variant="filled"
            >
              {toast.message}
            </Alert>
          </Snackbar>
        </div>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
